import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Dashboard } from './components/Dashboard';
import { DashboardListProvider } from './components/DashboardListProvider';
import { DashboardEditorOverlay } from './components/DashboardEditorOverlay';
import { initI18n } from './i18n';
import type { Dashboard as DashboardType } from './types/dashboard';

interface MountOptions {
  initialConfig: DashboardType;
  isEditable?: boolean;
  contextKey?: string;
  translations?: Record<string, string>;
  locale?: string;
}

const mountedRoots = new Map<string, Root>();

function mountDashboard(selector: string, options: MountOptions): void {
  const container = document.querySelector(selector);
  if (!container) return;

  if (mountedRoots.has(selector)) {
    mountedRoots.get(selector)?.unmount();
    mountedRoots.delete(selector);
  }

  container.innerHTML = '';

  const root = createRoot(container);
  mountedRoots.set(selector, root);

  if (options.translations && options.locale) {
    initI18n(options.translations, options.locale);
  }

  root.render(
    React.createElement(Dashboard, {
      initialConfig: options.initialConfig,
      isEditable: options.isEditable ?? true,
      contextKey: options.contextKey,
    })
  );
}

function unmountDashboard(selector: string): void {
  const root = mountedRoots.get(selector);
  if (root) {
    root.unmount();
    mountedRoots.delete(selector);
  }
}

function initDashboards(): void {
  document.body.setAttribute('data-dbg-v', 'CALLED');
  const containers = window.once('dashboard-react-init', '.dashboard-app[data-config]');
  if (!containers.length) return;

  try {
    const firstContainer = containers[0];
    const locale = firstContainer.dataset.locale || 'ru';
    const firstConfigAttr = firstContainer.getAttribute('data-config');
    if (firstConfigAttr) {
      const firstConfig = JSON.parse(firstConfigAttr);
      if (firstConfig && typeof firstConfig === 'object') {
        const translations = firstConfig.translations || {};
        initI18n(translations, locale);
      }
    }
  } catch (err) {
    console.error('[Dashboard React] Failed to initialize i18n:', err);
  }

  containers.forEach((container, index) => {
    try {
      const configAttr = container.getAttribute('data-config');
      if (!configAttr) return;

      const config = JSON.parse(configAttr);

      if (!config || typeof config !== 'object') return;
      if (!config.sourceKey) return;
      if (!config.panels || !Array.isArray(config.panels)) return;

      const isEditable = container.getAttribute('data-editable') !== 'false';
      const contextKey = container.getAttribute('data-context-key') || '';

      const uniqueSelector = contextKey
        ? `.dashboard-app[data-context-key="${CSS.escape(contextKey)}"]`
        : `.dashboard-app[data-config]:nth-of-type(${index + 1})`;

      mountDashboard(uniqueSelector, {
        initialConfig: config,
        isEditable,
        contextKey,
      });
    } catch (err) {
      console.error('[Dashboard React] Failed to mount:', err);
    }
  });
}

let dashboardsListRoot: Root | null = null;

function initDashboardsList(): void {
  const root = document.getElementById('dashboards-root');
  if (!root) {
    document.body.setAttribute('data-dbg', 'NO-ROOT');
    return;
  }
  if (dashboardsListRoot) return;

  // Get locale from <html lang="..."> with fallback
  const locale = document.documentElement.lang || 'ru';

  // Initialize i18n with empty translations so i18next is ready
  initI18n({}, locale);

  // Fetch dashboard translations from server
  fetch(`/api/translations/dashboard?locale=${locale}`)
    .then(r => r.json())
    .then(translations => {
      if (translations && typeof translations === 'object') {
        initI18n(translations, locale);
      }
    })
    .catch(() => {
      console.warn('[Dashboard React] Failed to fetch translations');
    });

  document.body.setAttribute('data-dbg', 'ROOT-FOUND');
  try {
    dashboardsListRoot = createRoot(root);
    dashboardsListRoot.render(
      React.createElement(React.Fragment, null,
        React.createElement(DashboardListProvider),
        React.createElement(DashboardEditorOverlay),
      )
    );
    document.body.setAttribute('data-dbg', 'RENDER-OK');
  } catch (e) {
    document.body.setAttribute('data-dbg', 'ERROR: ' + (e instanceof Error ? e.message : String(e)));
  }
}

// Drupal may load this script after DOMContentLoaded has already fired.
// Handle both cases: DOM still loading → wait for event; DOM ready → run immediately.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initDashboards();
    initDashboardsList();
  });
} else {
  initDashboards();
  initDashboardsList();
}

declare global {
  interface Window {
    mountDashboard: typeof mountDashboard;
    unmountDashboard: typeof unmountDashboard;
    Drupal: {
      behaviors: Record<string, {
        attach: (context: Document | HTMLElement, settings?: Record<string, unknown>) => void;
        detach?: (context: Document | HTMLElement, settings?: Record<string, unknown>) => void;
      }>;
      ajax?: unknown;
    };
    once: (id: string, selector: string, context?: ParentNode) => HTMLElement[];
  }
}

window.mountDashboard = mountDashboard;
window.unmountDashboard = unmountDashboard;

// Register Drupal behavior for AJAX re-initialization.
// This ensures React dashboards are re-mounted after any AJAX update
// (Facets filtering, Views AJAX, or any Drupal.ajax replacement).
if (typeof window.Drupal !== 'undefined') {
  window.Drupal.behaviors.dashboardReact = {
    attach(context: Document | HTMLElement) {
      const containers = window.once('dashboard-react', '.dashboard-app[data-config]', context);
      if (!containers.length) return;

      // Re-init i18n from first new container if needed
      try {
        const firstContainer = containers[0] as HTMLElement;
        const firstConfigAttr = firstContainer.getAttribute('data-config');
        const firstConfig = firstConfigAttr ? JSON.parse(firstConfigAttr) : null;
        const locale = firstContainer.dataset.locale || 'ru';
        const translations = firstConfig?.translations || {};
        initI18n(translations, locale);
      } catch (err) {
        console.error('[Dashboard React] Failed to i18n in attach:', err);
      }

      containers.forEach((container, index) => {
        try {
          const configAttr = container.getAttribute('data-config');
          if (!configAttr) return;

          const config = JSON.parse(configAttr);
          if (!config || typeof config !== 'object') return;
          if (!config.sourceKey) return;
          if (!config.panels || !Array.isArray(config.panels)) return;

          const isEditable = container.getAttribute('data-editable') !== 'false';
          const contextKey = container.getAttribute('data-context-key') || '';

          const uniqueSelector = contextKey
            ? `.dashboard-app[data-context-key="${CSS.escape(contextKey)}"]`
            : `.dashboard-app[data-config]:nth-of-type(${index + 1})`;

          mountDashboard(uniqueSelector, {
            initialConfig: config,
            isEditable,
            contextKey,
          });
        } catch (err) {
          console.error('[Dashboard React] Failed to mount in attach:', err);
        }
      });
    },
  };
}

export { mountDashboard, unmountDashboard };
